import { useSetToast } from "@app/components/Toast";
import { extractContentTypeFromUrl } from "@app/shared/utils/file";
import { useConditionalScrollTo } from "@app/shared/utils/scroll";
import { getFileFormatCategoryFromMimeType } from "@app/usecases/VersionUseCases/utils";
import { type AttachmentType } from "@app/utils/ai/chat";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button, type ButtonProps } from "@design-system/Inputs/Button";
import { Chip, type ChipProps } from "@design-system/Inputs/Chip";
import { Box } from "@design-system/Layout/Box";
import {
    FlexCol,
    FlexRow,
    type OrientedFlexProps,
} from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text, type TextProps } from "@design-system/Typography/Text";
import { cn, isOverflown } from "@design-system/Utilities";
import { ScoreValue } from "@generated/client/graphql";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import {
    createContext,
    type RefObject,
    useContext,
    useMemo,
    useRef,
    type FC,
    type Ref,
    useState,
    useCallback,
    type MouseEventHandler,
    useEffect,
} from "react";
import { Link } from "react-router-dom";
import { ExtensionTag } from "../../ExtensionTag";
import { MarkdownText } from "../../MarkdownText/MarkdownText";
import { useAgentChatTranslations } from "./i18n";
import { Input } from "./Input";
import { debounce } from "lodash";

const ChatCtx = createContext<{
    contentCbRef: (node: HTMLDivElement) => void;
    onContentUnmount: () => void;
    isScrollButtonVisible: boolean;
    scrollRef: RefObject<{
        scrollToBottom: () => void;
    } | null>;
}>({
    contentCbRef: () => {},
    onContentUnmount: () => {},
    isScrollButtonVisible: false,
    scrollRef: {
        current: {
            scrollToBottom: () => {},
        },
    },
});

export const useChatCtx = () => {
    return useContext(ChatCtx);
};

function useButtonScrollCtx() {
    const { isScrollButtonVisible, scrollRef } = useChatCtx();

    const scrollToBottom = () => {
        scrollRef?.current?.scrollToBottom();
    };

    return {
        isScrollButtonVisible,
        scrollToBottom,
    };
}

function useContentScrollCtx() {
    const { contentCbRef, onContentUnmount } = useChatCtx();

    return {
        contentCbRef,
        onContentUnmount,
    };
}

const ScrollToBottomButton: FC<
    ButtonProps & { ref?: Ref<HTMLButtonElement | null> }
> = ({ onClick, ref, ...props }) => {
    const { isScrollButtonVisible, scrollToBottom } = useButtonScrollCtx();

    const handleClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
        scrollToBottom();
        onClick?.(evt);
    };

    if (!isScrollButtonVisible) return null;

    return (
        <FlexRow justifyContent="center" maxH="0">
            <Button
                className="rounded-full -translate-y-7 self-center"
                variant="outlined"
                ref={ref}
                size="sm"
                onClick={handleClick}
                {...props}
            >
                <Button.Icon name="arrowDown" />
            </Button>
        </FlexRow>
    );
};

const Content: FC<
    OrientedFlexProps & { ref?: RefObject<HTMLDivElement | null> }
> = ({ className, children, ...props }) => {
    const { contentCbRef, onContentUnmount } = useContentScrollCtx();

    useEffect(() => {
        return () => {
            onContentUnmount();
        };
    }, [onContentUnmount]);

    return (
        <FlexCol
            gap="6"
            h="full"
            className={cn(
                "[&>div:last-of-type]:min-h-[calc(100%-60px)]",
                "[&>div:last-of-type_.AiMsgBottom]:!block",
                "relative",
                "overflow-y-auto",
                "w-full",
                "px-[calc(50%-400px)]",
                className,
            )}
            ref={contentCbRef}
            {...props}
        >
            {children}
        </FlexCol>
    );
};

const Header = FlexRow;

export const TitleChip: FC<Omit<ChipProps, "children"> & { title: string }> = ({
    className,
    title,
    ...props
}) => {
    return (
        <Chip
            {...props}
            size={props.size ?? "md"}
            interactive={props.interactive ?? false}
            className={cn("bg-white", className)}
        >
            <Chip.Icon name="clipboardList" />
            <Chip.Value>{title}</Chip.Value>
        </Chip>
    );
};

const Date: FC<Omit<TextProps, "children"> & { date: Date }> = ({
    date,
    ...props
}) => {
    dayjs.extend(LocalizedFormat);
    const displayedDate = dayjs(date).format("MMM D, hh:mm A");
    return (
        <FlexRow justifyContent="center">
            <Text variant="body-sm" color="secondary" {...props}>
                {displayedDate}
            </Text>
        </FlexRow>
    );
};

const UserMessage: FC<
    OrientedFlexProps & {
        scrollToMessage?: boolean;
        attachment?: AttachmentType | undefined;
    }
> = ({ className, children, scrollToMessage, attachment, ...props }) => {
    const scrollToMessageRef = useConditionalScrollTo(scrollToMessage);

    const isPdf = useMemo(() => {
        const fileType = extractContentTypeFromUrl(attachment?.url);
        return fileType === "application/pdf";
    }, [attachment?.url]);

    return (
        <FlexRow justifyContent="end">
            <FlexCol
                px="4"
                py="2"
                gap="1"
                elevation="b-100"
                h="fit"
                className={cn(
                    "bg-quarternary rounded-[18px] max-w-[500px]",
                    attachment ? "w-[272px] max-w-[272px]" : "",
                    className,
                )}
                {...props}
            >
                {attachment && (
                    <Link
                        to={attachment.url}
                        target="_blank"
                        className="[all:unset]"
                    >
                        <FlexCol
                            gap="1.5"
                            br="lg"
                            alignItems="start"
                            className="w-[240px] group"
                        >
                            <Ellipsis asChild>
                                <Text
                                    variant="body-bold"
                                    color="primary"
                                    className="cursor-pointer max-w-[240px] group group-hover:underline"
                                >
                                    {attachment.name}
                                </Text>
                            </Ellipsis>
                            <Box
                                className="flex h-[200px] w-[240px] bg-white group"
                                br="lg"
                                alignItems="center"
                                justifyContent="center"
                            >
                                {isPdf ? (
                                    <iframe
                                        src={attachment.url}
                                        className="w-full h-full"
                                    />
                                ) : attachment.contentType ? (
                                    <ExtensionTag
                                        name={getFileFormatCategoryFromMimeType(
                                            attachment.contentType,
                                        )}
                                        className="w-8 h-8"
                                    />
                                ) : (
                                    <Box
                                        w="7"
                                        minH="7"
                                        minW="7"
                                        h="7"
                                        br="full"
                                        className="flex items-center justify-center bg-tertiary"
                                    >
                                        <Icon name="paperclip" size="sm" />
                                    </Box>
                                )}
                            </Box>
                        </FlexCol>
                    </Link>
                )}
                <FlexCol>
                    <Text variant="body" color="primary">
                        {children ?? attachment?.name}
                    </Text>
                    <div ref={scrollToMessageRef} />
                </FlexCol>
            </FlexCol>
        </FlexRow>
    );
};

const AiMessage: FC<
    Omit<OrientedFlexProps, "children"> & {
        ref?: Ref<HTMLDivElement>;
        children?: string | undefined;
        withCopy?: boolean;
        onRegenerate?: () => void;
        onFeedback?: (feedback: ScoreValue) => void;
    }
> = ({
    className,
    children: message,
    withCopy,
    onRegenerate,
    onFeedback,
    ...props
}) => {
    const { t, keys } = useAgentChatTranslations();
    const { setToastSuccess } = useSetToast();

    const [feedback, setFeedback] = useState<ScoreValue | null>(null);

    const copyToClipboard = () => {
        if (message) {
            navigator.clipboard.writeText(message);
            setToastSuccess(t(keys.copied_to_clipboard));
        }
    };

    const handleFeedback = (feedback: ScoreValue) => {
        return () => {
            setFeedback(feedback);
            onFeedback?.(feedback);
        };
    };

    return (
        <FlexCol
            justifyContent="start"
            gap="4"
            w="full"
            className={className}
            {...props}
        >
            <MarkdownText tableCopy>{message}</MarkdownText>
            <FlexRow alignItems="center" justifyContent="between">
                <FlexRow gap="2" alignItems="center">
                    {withCopy && (
                        <Button
                            variant="outlined"
                            size="sm"
                            className="w-fit"
                            onClick={copyToClipboard}
                        >
                            {t(keys.copy)}
                            <Button.Icon name="duplicate" />
                        </Button>
                    )}
                    {onRegenerate && (
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Button
                                    variant="text"
                                    size="sm"
                                    className="w-fit"
                                    onClick={onRegenerate}
                                >
                                    <Button.Icon name="arrowCircleRight" />
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.tooltip_regenerate)}
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                </FlexRow>
                {onFeedback && (
                    <FlexRow gap="2" alignItems="center">
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Button
                                    variant="text"
                                    size="sm"
                                    className="w-fit"
                                    onClick={handleFeedback(ScoreValue.Up)}
                                >
                                    <Button.Icon
                                        name={
                                            feedback === ScoreValue.Up
                                                ? "thumbsUpFilled"
                                                : "thumbsUp"
                                        }
                                    />
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.tooltip_positive_feedback)}
                            </Tooltip.Content>
                        </Tooltip>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={handleFeedback(ScoreValue.Down)}
                                >
                                    <Button.Icon
                                        name={
                                            feedback === ScoreValue.Down
                                                ? "thumbsDownFilled"
                                                : "thumbsDown"
                                        }
                                    />
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.tooltip_negative_feedback)}
                            </Tooltip.Content>
                        </Tooltip>
                    </FlexRow>
                )}
            </FlexRow>
            <Box className="AiMsgBottom hidden" minH="5" />
        </FlexCol>
    );
};

const ErrorMessage: FC<TextProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    children,
    ...props
}) => {
    const { t, keys } = useAgentChatTranslations();

    const handleReload = () => {
        window.location.reload();
    };

    return (
        <FlexCol justifyContent="start" className="w-fit" ref={ref}>
            <FlexCol gap="4">
                <Text
                    variant="body"
                    color="danger"
                    className="whitespace-pre-line"
                    {...props}
                >
                    {children ?? t(keys.error_message)}
                </Text>
                <Button
                    variant="outlined"
                    size="sm"
                    className="w-fit"
                    onClick={handleReload}
                >
                    {t(keys.error_button_reload)}
                    <Button.Icon name="arrowCircleRight" />
                </Button>
            </FlexCol>
        </FlexCol>
    );
};

const LoadingMessage: FC<TextProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    children,
    ...props
}) => {
    const { t, keys } = useAgentChatTranslations();
    return (
        <FlexRow gap="2" ref={ref}>
            <Text variant="body-lg" color="tertiary" {...props}>
                {children ?? t(keys.loading)}
            </Text>
            <Icon
                name="loaderAnimated"
                size="lg"
                className="text-accent-1-300"
            />
        </FlexRow>
    );
};

const EmptyState: FC<
    OrientedFlexProps & {
        title: string;
        description?: string;
        imageUrl?: string;
        ref?: Ref<HTMLDivElement>;
    }
> = ({ title, description, imageUrl, ...props }) => {
    const { t, keys } = useAgentChatTranslations();
    return (
        <FlexCol
            gap="4"
            h="full"
            w="full"
            alignItems="center"
            justifyContent="center"
            {...props}
        >
            {!!imageUrl && (
                <img src={imageUrl} className=" w-18 h-18 rounded-full" />
            )}
            <FlexCol gap="1.5" alignItems="center">
                <Text variant="header1" color="primary">
                    {title}
                </Text>
                <FlexRow gap="2" alignItems="center" justifyContent="center">
                    <Icon
                        name="stars"
                        size="lg"
                        className="text-accent-1-300"
                    />
                    <Text variant="body-lg" color="primary">
                        {description ?? t(keys.empty_state_description)}
                    </Text>
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );
};

const ExampleMessageSection: FC<
    OrientedFlexProps & { ref?: Ref<HTMLDivElement> }
> = ({ className, children, ...props }) => {
    return (
        <FlexCol
            gap="2"
            mt="4"
            className={cn("w-[800px]", className)}
            {...props}
        >
            {children}
        </FlexCol>
    );
};

const ExampleMessageChip: FC<ChipProps & { ref?: Ref<HTMLButtonElement> }> = ({
    children,
    ...props
}) => {
    return (
        <Chip {...props} className="w-fit">
            <Chip.Label>{children}</Chip.Label>
        </Chip>
    );
};

type ChatType = FC<OrientedFlexProps & { ref?: Ref<HTMLDivElement> }> & {
    Content: typeof Content;
    Date: typeof Date;
    AiMessage: typeof AiMessage;
    UserMessage: typeof UserMessage;
    ErrorMessage: typeof ErrorMessage;
    LoadingMessage: typeof LoadingMessage;
    Input: typeof Input;
    EmptyState: typeof EmptyState;
    ExampleMessageChip: typeof ExampleMessageChip;
    Header: typeof Header;
    TitleChip: typeof TitleChip;
    ScrollToBottomButton: typeof ScrollToBottomButton;
    ExampleMessageSection: typeof ExampleMessageSection;
};

function checkIsScrollButtonVisible(node: HTMLDivElement) {
    const isContainerOverflowing = isOverflown(node);
    const isScrolledToBottom =
        node.scrollTop === node.scrollHeight - node.offsetHeight;

    return isContainerOverflowing && !isScrolledToBottom;
}

function useCreateCtxData() {
    const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);
    const mutationObserver = useRef<MutationObserver | null>(null);
    const scrollRef = useRef<{
        scrollToBottom: () => void;
    } | null>(null);
    const onContentUnmount = useCallback(() => {
        mutationObserver.current?.disconnect();
    }, []);

    const contentCbRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        function handleScroll() {
            setIsScrollButtonVisible(checkIsScrollButtonVisible(node));
        }

        const debouncedHandleScroll = debounce(handleScroll, 50);

        node.addEventListener("scroll", debouncedHandleScroll);

        mutationObserver.current = new MutationObserver(debouncedHandleScroll);
        mutationObserver.current.observe(node, {
            childList: true,
            subtree: true,
        });

        scrollRef.current = {
            scrollToBottom: () => {
                node.scrollTo({
                    top: node.scrollHeight,
                    behavior: "smooth",
                });
            },
        };
    }, []);

    return {
        isScrollButtonVisible,
        onContentUnmount,
        contentCbRef,
        scrollRef,
    };
}

export const Chat: ChatType = ({ className, ...props }) => {
    const { isScrollButtonVisible, onContentUnmount, contentCbRef, scrollRef } =
        useCreateCtxData();

    return (
        <ChatCtx
            value={{
                contentCbRef,
                isScrollButtonVisible,
                onContentUnmount,
                scrollRef,
            }}
        >
            <FlexCol
                h="full"
                w="full"
                justifyContent="between"
                alignItems="center"
                className={cn("bg-white", className)}
                {...props}
            />
        </ChatCtx>
    );
};

Chat.Content = Content;
Chat.Date = Date;
Chat.AiMessage = AiMessage;
Chat.UserMessage = UserMessage;
Chat.ErrorMessage = ErrorMessage;
Chat.LoadingMessage = LoadingMessage;
Chat.Input = Input;
Chat.EmptyState = EmptyState;
Chat.ExampleMessageChip = ExampleMessageChip;
Chat.Header = Header;
Chat.TitleChip = TitleChip;
Chat.ScrollToBottomButton = ScrollToBottomButton;
Chat.ExampleMessageSection = ExampleMessageSection;
